<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Invoice Payments</h5>
          <p class="page-sub">History of all invoice payment transactions</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search payments..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="JSON.parse(status.successful)">Successful</option>
            <option :value="JSON.parse(status.failed)">Failed</option>
            <option :value="JSON.parse(status.pending)">Pending</option>
            <option :value="JSON.parse(status.all)">All Statuses</option>
          </select>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'l', 'invoice_payments', 'Invoice Payments')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('invoice_payments.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading payments...</p>
        </div>
  
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="money-bill-wave" /></div>
          <p class="state-title">No payments found</p>
          <p class="state-sub">Your payment history is empty.</p>
        </div>
  
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option><option value="10">10</option><option value="20">20</option>
                <option value="30">30</option><option value="50">50</option><option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Invoice</th>
                  <th>Payee</th>
                  <th class="text-right">Expected</th>
                  <th class="text-right">Paid</th>
                  <th class="text-right">Balance</th>
                  <th>Gateway</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th class="col-actions">View</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="index">
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td>
                    <span class="batch-code">{{ row?.invoice?.invoice_no }}</span>
                  </td>
  
                  <td>
                    <div class="payee-cell">
                      <div class="payee-avatar">{{ payeeInitials(row) }}</div>
                      <div>
                        <p class="payee-name">{{ row.payee_name || `${row?.user?.first_name} ${row?.user?.last_name}` }}</p>
                        <p class="payee-sub">{{ row?.payee_email || '—' }}</p>
                      </div>
                    </div>
                  </td>
  
                  <td class="text-right text-muted-cell">{{ row?.invoice?.currency?.symbol }}{{ formatPrice(row.expected_amount) }}</td>
  
                  <td class="text-right earning-value fw-bold">{{ row?.invoice?.currency?.symbol }}{{ formatPrice(row.paid_amount) }}</td>
  
                  <td class="text-right" :class="row.balance_amount > 0 ? 'deduction-value fw-bold' : 'text-muted-cell'">
                    {{ row?.invoice?.currency?.symbol }}{{ formatPrice(row.balance_amount) }}
                  </td>
  
                  <td>
                    <span class="gateway-pill" :class="row?.gateway ? 'gateway-pill--online' : 'gateway-pill--manual'">
                      <font-awesome-icon :icon="row?.gateway ? 'globe' : 'pen'" class="me-1" />
                      {{ row?.gateway?.name || 'Manual' }}
                    </span>
                  </td>
  
                  <td>
                    <span class="status-badge" :class="paymentStatusClass(row.status)">
                      {{ getStatus(row) }}
                    </span>
                  </td>
  
                  <td class="text-muted-cell" style="white-space:nowrap">{{ formatDate(row.payment_date) }}</td>
  
                  <td class="col-actions">
                    <router-link
                      :to="$url('/invoice/detail/' + row?.invoice?.id)"
                      class="action-btn action-btn--view"
                      :class="permission.view_invoice != 1 ? 'disabled' : ''"
                      title="View Invoice"
                    >
                      <font-awesome-icon icon="eye" />
                    </router-link>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
  
        </template>
      </div>
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { reusableMixin } from '../../../lib/reusableMixin';
  import { useStore } from 'vuex';
  import moment from 'moment';
  import AdminHeader from './component/header.vue';
  
  export default {
    components: { AdminHeader },
    mixins: [reusableMixin],
  
    data() {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
      return {
        print_content: [],
        headerArrayColumn: ['S/N','Invoice Number','Payee Name','Payee Email','Expected Amount','Total Paid','Total Balance','Currency','Payment Date','Gateway','Status'],
        details:      [],
        loading:      false,
        currentData:  {},
  
        status: {
          all:        "['0','1','2']".replace(/'/g, '"'),
          pending:    "['0']".replace(/'/g, '"'),
          successful: "['1']".replace(/'/g, '"'),
          failed:     "['2']".replace(/'/g, '"'),
        },
  
        tableData: { page: 1,
          start_date: sixMonthsAgo, end_date: new Date(),
          draw: 0, length: 10, search: '', column: 0, dir: 'desc', status: [0,1,2],
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() { this.fetchData(); },
  
    methods: {
      getStatus(data) {
        return ['Pending','Successful','Failed'][data.status] ?? '—';
      },
  
      paymentStatusClass(status) {
        return ['status--pending','status--success','status--failed'][status] ?? '';
      },
  
      payeeInitials(row) {
        const name = row.payee_name || `${row?.user?.first_name ?? ''} ${row?.user?.last_name ?? ''}`;
        return name.trim().split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || '?';
      },
  
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({
          sn:              idx + 1,
          invoice_no:      d?.invoice?.invoice_no,
          payee_name:      d.payee_name,
          payee_email:     d.payee_email,
          expected_amount: this.formatPrice(d.expected_amount),
          paid_amount:     this.formatPrice(d.paid_amount),
          balance_amount:  this.formatPrice(d.balance_amount),
          currency:        `${d?.invoice?.currency?.name} (${d?.invoice?.currency?.code})`,
          payment_date:    d.payment_date,
          gateway:         d.gateway?.name ?? 'Manual',
          status:          this.getStatus(d),
        }));
      },
  
      async fetchData(url = '/invoice/payments') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.details = response.data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
            this.loading = false;
          })
          .catch(() => { this.loading = false; });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
  
      formatDate(date) { return moment(date).format('MMM DD, YYYY'); },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  .me-2 { margin-right:8px; } .mt-3 { margin-top:12px; }
  
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell { color:#4a5e78; }
  
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13px; vertical-align:middle; }
  .col-actions { width:48px; text-align:center; }
  .text-right  { text-align:right; }
  .fw-bold     { font-weight:700; }
  .earning-value   { color:#16a34a; }
  .deduction-value { color:#e03535; }
  
  .batch-code { font-family:monospace; font-size:12px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Payee Cell ── */
  .payee-cell   { display:flex; align-items:center; gap:9px; }
  .payee-avatar { width:32px; height:32px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
  .payee-name   { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 2px; }
  .payee-sub    { font-size:11.5px; color:#7a90b0; margin:0; }
  
  /* ── Gateway pill ── */
  .gateway-pill        { display:inline-flex; align-items:center; border-radius:6px; padding:3px 9px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .gateway-pill--online { background:#edf4ff; color:#004AAD; border-color:#c5d6f5; }
  .gateway-pill--manual { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }
  .me-1 { margin-right:4px; }
  
  /* ── Status Badges ── */
  .status-badge    { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
  .status--pending { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .status--success { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .status--failed  { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  
  /* ── Action Button ── */
  .action-btn  { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:13px; cursor:pointer; transition:background .15s; text-decoration:none; background:#fff; margin:0 auto; }
  .action-btn.disabled { opacity:.4; pointer-events:none; }
  .action-btn--view { color:#0d9488; }
  .action-btn--view:hover:not(.disabled) { background:#f0fdfb; border-color:#a7f3d0; text-decoration:none; }
  
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } }
  </style>